import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { News } from 'src/app/core/models/news';
import { NewsService } from 'src/app/core/services/news.service';

type SortByOption = 'relevance' | 'newest' | 'oldest';

interface SortMenuItem {
  label: string;
  value: SortByOption;
}

@Component({
  selector: 'app-news-search',
  templateUrl: './news-search.component.html',
  styleUrls: ['./news-search.component.scss'],
})
export class NewsSearchComponent implements OnInit {
  news: News[] = [];
  loading = true;
  loadingNews = true;
  sortingNews = false;
  loadingPagination = false;
  searchQuery = '';

  // Pagination properties
  currentPage = 0;
  pageSize = 5;
  totalItems = 0;
  pageSizeOptions = [5, 10, 20];
  rangeStart = 0;
  rangeEnd = 0;

  readonly itemsMenu: SortMenuItem[] = [
    { label: 'SortByRelevance', value: 'relevance' },
    { label: 'SortByNewest', value: 'newest' },
    { label: 'SortByOldest', value: 'oldest' },
  ];

  sortBy: SortMenuItem = this.itemsMenu[0];

  constructor(
    private newsService: NewsService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  async ngOnInit(): Promise<void> {
    this.loading = true;
    this.searchQuery = this.route.snapshot.queryParams['q'] || '';
    await this.loadNews();
  }

  async loadNews(): Promise<void> {
    if (!this.searchQuery) {
      this.sortBy = this.itemsMenu[0];
    }
    try {
      const result = await this.newsService.searchNews({
        limit: this.pageSize,
        page: this.currentPage,
        searchText: this.searchQuery,
        sortBy: this.sortBy.value,
      });

      this.news = result.data;
      this.totalItems = result.count;
      this.updateRangeLabel();
    } catch (error) {
      console.error('Error loading news:', error);
      this.news = [];
      this.totalItems = 0;
      this.updateRangeLabel();
    } finally {
      this.loading = false;
      this.loadingNews = false;
      this.sortingNews = false;
      this.loadingPagination = false;
    }
  }

  async onSearchChange(query: string): Promise<void> {
    const next = query.trim();
    const prev = this.searchQuery.trim();
    if (next === prev) {
      return;
    }

    this.loadingNews = true;
    this.searchQuery = query;
    this.currentPage = 0;

    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: query || null },
      queryParamsHandling: 'merge',
    });

    await this.loadNews();
  }

  goToNews(news: News) {
    if (news.metadata.link) {
      window.open(news.metadata.link, '_blank');
    } else {
      this.router.navigate([`client/news/${news.id}`]);
    }
  }

  goBack() {
    this.router.navigate(['client/news']);
  }

  sortNewsBy(value: SortMenuItem) {
    this.sortingNews = true;
    this.sortBy = value;
    this.currentPage = 0;
    this.loadNews();
  }

  private updateRangeLabel(): void {
    if (this.totalItems === 0) {
      this.rangeStart = 0;
      this.rangeEnd = 0;
      return;
    }
    this.rangeStart = this.currentPage * this.pageSize + 1;
    this.rangeEnd = Math.min((this.currentPage + 1) * this.pageSize, this.totalItems);
  }

  isLastPage(): boolean {
    return (this.currentPage + 1) * this.pageSize >= this.totalItems;
  }

  previousPage(): void {
    if (this.currentPage > 0) {
      this.loadingPagination = true;
      this.currentPage--;
      this.loadNews();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  nextPage(): void {
    if (!this.isLastPage()) {
      this.loadingPagination = true;
      this.currentPage++;
      this.loadNews();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  onPageSizeChange(size: number): void {
    this.loadingPagination = true;
    this.pageSize = size;
    this.currentPage = 0;
    this.loadNews();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
